import { useEffect, useRef } from "react";
import type { GuessRow, KeyState } from "../wordle/keystate";
import { HandGrid } from "./HandGrid";
import { NotebookCell } from "./NotebookCell";
import { gradeFor, type NotebookStatus, teacherNote } from "./teacher";
import "./notebook.css";

/** Gap between marks along a row; the last stroke plus a beat closes the reveal. */
export const NOTEBOOK_STAGGER_MS = 260;
export const NOTEBOOK_REVEAL_TOTAL_MS = 4 * NOTEBOOK_STAGGER_MS + 420;
const ROWS = 6;

/** Width-bound on wide screens, height-bound on short ones (6 rows of square cells). */
function boardWidth(cols: number, reservedPx: number): string {
  return `min(372px, calc((var(--tg-viewport-stable-height, 100vh) - ${reservedPx}px) * ${cols / ROWS} + 34px))`;
}

interface NotebookBoardProps {
  number: number;
  guesses: GuessRow[];
  current: string;
  revealRow: number | null;
  shakeRow: number | null;
  finished: boolean;
  /** Status and attempts as the teacher already sees them (they wait for the reveal). */
  status: NotebookStatus;
  attempts: number;
  answer: string | undefined;
  /** A rejected submit's message, written in red while fresh. */
  reject: string | null;
  cols?: number;
  reservedPx: number;
  /** One highlighter stroke landed — the haptic tick. */
  onTick?: () => void;
}

/**
 * Словко as a dictation: a hand-drawn table on notebook paper (light) or the
 * classroom blackboard (dark). Marks are highlighter swipes; the teacher slaps a
 * grade sticker (12-point scale) over the finished row and writes one line
 * under the table.
 */
export function NotebookBoard({
  number,
  guesses,
  current,
  revealRow,
  shakeRow,
  finished,
  status,
  attempts,
  answer,
  reject,
  cols = 5,
  reservedPx,
  onTick,
}: NotebookBoardProps) {
  // Tick once per stroke of a fresh reveal (not on restore).
  useEffect(() => {
    if (revealRow === null || !onTick) return undefined;
    const timers = Array.from({ length: cols }, (_, c) => window.setTimeout(onTick, c * NOTEBOOK_STAGGER_MS + 260));
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [revealRow, cols, onTick]);

  // The sticker slaps on only when the grade appears during play, not when a finished
  // day reopens. Latched, so a re-render mid-animation can't cut it.
  const seenStatus = useRef(status);
  const drawLatch = useRef(false);
  if (seenStatus.current === "playing" && status !== "playing") drawLatch.current = true;
  const drawGrade = drawLatch.current;
  useEffect(() => {
    if (status !== "playing") seenStatus.current = status;
  }, [status]);

  const grade = gradeFor(status, attempts);
  const gradeRow = grade === null ? null : status === "lost" ? ROWS - 1 : attempts - 1;
  const note = teacherNote(status, attempts, answer, reject);

  return (
    <div className="nb mx-auto w-full px-[14px] pb-1.5 pt-2" style={{ maxWidth: boardWidth(cols, reservedPx) }}>
      <div className="nb-heading text-center">Диктант №{number}</div>
      <div className="nb-rows relative mt-2.5">
        <HandGrid cols={cols} rows={ROWS} />
        {Array.from({ length: ROWS }, (_, r) => {
          const g = guesses[r];
          const isActive = r === guesses.length && !finished;
          const letters = g
            ? g.word.split("")
            : isActive
              ? current.padEnd(cols, " ").split("").map((ch) => ch.trim())
              : Array.from({ length: cols }, () => "");
          const marks = g ? (g.marks.split("") as KeyState[]) : [];
          return (
            <div
              key={r}
              className={`nb-row grid ${shakeRow === r ? "row-shake nb-row-wrong" : ""}`}
              style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
            >
              {letters.map((ch, c) => (
                <NotebookCell
                  key={c}
                  letter={ch}
                  state={marks[c] ?? null}
                  reveal={revealRow === r}
                  delay={c * NOTEBOOK_STAGGER_MS}
                  write={isActive}
                  tilt={(((r * 7 + c * 13) % 7) - 3) * 0.9}
                  lift={(((r * 5 + c * 3) % 5) - 2) * 0.6}
                />
              ))}
            </div>
          );
        })}
        {gradeRow !== null && grade !== null && (
          <div
            className={`nb-sticker ${drawGrade ? "nb-sticker-slap" : ""} ${status === "lost" ? "nb-sticker-lost" : ""}`}
            // on the row's bottom-right corner: half on the finished row, half below it
            style={{ top: `${((gradeRow + 0.9) / ROWS) * 100}%` }}
            aria-label={`оцінка ${grade}`}
          >
            <span>{grade}</span>
          </div>
        )}
      </div>
      <div className={`nb-note mt-1 ${note?.pen === "pencil" ? "nb-note-pencil" : ""}`} role="status">
        {note && <span key={note.text}>{note.text}</span>}
      </div>
    </div>
  );
}
